import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Flex } from '@dutchie/common/components/rebass-compat';

import { ModalContainer, ModalClose } from 'shared/modals';
import Imgix from 'shared/components/imgix';
import type { ImgixComponentProps } from 'shared/components/imgix';
import { ImgixDefaultSettings } from 'shared/constants/content';
import Search from 'src/dispensary-admin/common/search';
import Dropdown from 'shared/components/dropdown';

import { useLibraryState } from 'src/enterprise-admin/assets/library/hooks/use-library-state';
import { UploadZone } from 'src/enterprise-admin/assets/library/components/upload-zone';
import { UploadButton } from 'src/enterprise-admin/assets/library/components/upload-button';
import type { AssetType, LibraryAsset } from 'src/enterprise-admin/assets/library/types';
import { buildFolderTree } from 'src/enterprise-admin/assets/library/utils/folder-tree';
import type { FolderNode } from 'src/enterprise-admin/assets/library/utils/folder-tree';
import type { AssetLibraryModalProps, SelectedAsset } from './types';

const TYPE_OPTIONS: Array<{ key: string; label: string; value: AssetType | 'all' }> = [
  { key: 'all', label: 'All Types', value: 'all' },
  { key: 'image', label: 'Images', value: 'image' },
  { key: 'video', label: 'Videos', value: 'video' },
  { key: 'svg', label: 'SVGs', value: 'svg' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type GridContentArgs = {
  loading: boolean;
  assets: LibraryAsset[];
  selectedIds: Set<string>;
  onToggle: (a: LibraryAsset) => void;
  openFilePicker: () => void;
};

function renderGridContent({ loading, assets, selectedIds, onToggle, openFilePicker }: GridContentArgs): JSX.Element {
  if (loading && assets.length === 0) {
    return (
      <EmptyState>
        <EmptyText>Loading library…</EmptyText>
      </EmptyState>
    );
  }
  if (assets.length === 0) {
    return (
      <EmptyState>
        <EmptyText>No matching assets found.</EmptyText>
        <EmptyUploadLink type='button' onClick={openFilePicker}>
          Upload a new asset
        </EmptyUploadLink>
      </EmptyState>
    );
  }
  return (
    <Grid>
      {assets.map((asset) => (
        <Tile
          key={asset.id}
          data-testid='asset-library-tile'
          $selected={selectedIds.has(asset.id)}
          onClick={() => onToggle(asset)}
        >
          <TileThumb>
            <TileImg
              src={asset.url}
              imgixParams={{ ...ImgixDefaultSettings, w: 300, h: 200, fit: 'crop', crop: 'edges' }}
              htmlAttributes={{ alt: asset.name, loading: 'lazy' }}
            />
            <TileBadge>{formatBadgeLabel(asset.mimeType)}</TileBadge>
            {selectedIds.has(asset.id) && <SelectedCheck>✓</SelectedCheck>}
          </TileThumb>
          <TileInfo>
            <TileName title={asset.name}>{asset.name}</TileName>
            <TileMeta>
              {asset.width ?? '?'} × {asset.height ?? '?'} · {formatFileSize(asset.fileSize)}
            </TileMeta>
          </TileInfo>
        </Tile>
      ))}
    </Grid>
  );
}

/** True when `targetId` is one of the node's descendants (used to auto-reveal the active folder). */
function folderContainsActive(node: FolderNode, targetId: string | null): boolean {
  if (!targetId) return false;
  return node.children.some((child) => child.id === targetId || folderContainsActive(child, targetId));
}

function toSelectedAsset(asset: LibraryAsset): SelectedAsset {
  return {
    id: asset.id,
    url: asset.url,
    name: asset.name,
    width: asset.width,
    height: asset.height,
    mimeType: asset.mimeType,
  };
}

function formatBadgeLabel(mimeType: string): string {
  const sub = mimeType.split('/')[1] ?? '';
  if (sub === 'svg+xml') return 'SVG';
  if (sub === 'gif') return 'GIF';
  if (sub === 'png') return 'PNG';
  if (sub === 'webp') return 'WEBP';
  if (sub === 'mp4') return 'MP4';
  if (sub === 'jpeg') return 'JPG';
  return mimeType.split('/')[0]?.toUpperCase() ?? '';
}

export function AssetLibraryModal(props: AssetLibraryModalProps): JSX.Element {
  const {
    enterpriseId,
    isOpen,
    onClose,
    onSelect,
    multiSelect = false,
    title = 'Select from Asset Library',
    description = 'Choose an asset from your enterprise library',
    allowedTypes,
  } = props;
  const {
    state,
    allTags,
    totalAssets,
    setFilter,
    loading,
    error,
    refetchAssets,
    createFolder,
  } = useLibraryState(enterpriseId, { allowedTypes });
  // Insertion-ordered selection: single-select holds at most one entry, multi-select toggles many.
  const [selectedById, setSelectedById] = useState<Map<string, LibraryAsset>>(new Map());
  const [showUpload, setShowUpload] = useState(false);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());

  const folderTree = useMemo(() => buildFolderTree(state.folders), [state.folders]);
  const displayedAssets = useMemo(
    () => (allowedTypes ? state.assets.filter((asset) => allowedTypes.includes(asset.type)) : state.assets),
    [allowedTypes, state.assets]
  );
  const typeOptions = useMemo(
    () =>
      allowedTypes
        ? TYPE_OPTIONS.filter((option) => option.value !== 'all' && allowedTypes.includes(option.value))
        : TYPE_OPTIONS,
    [allowedTypes]
  );
  const selectedIds = useMemo(() => new Set(selectedById.keys()), [selectedById]);
  const selectedList = useMemo(() => Array.from(selectedById.values()), [selectedById]);

  const toggleSelected = useCallback(
    (asset: LibraryAsset) => {
      setSelectedById((prev) => {
        const next = new Map(prev);
        if (multiSelect) {
          if (next.has(asset.id)) next.delete(asset.id);
          else next.set(asset.id, asset);
        } else {
          next.clear();
          next.set(asset.id, asset);
        }
        return next;
      });
    },
    [multiSelect]
  );

  const toggleFolderExpand = useCallback((id: string) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const assets = selectedList.map(toSelectedAsset);
    if (assets.length === 0) return;
    // `onSelect`'s signature is correlated to `multiSelect` at the type level (discriminated union),
    // but TS can't track that through the destructured boolean, so cast at the call site.
    if (multiSelect) {
      (onSelect as (a: SelectedAsset[]) => void)(assets);
    } else {
      (onSelect as (a: SelectedAsset) => void)(assets[0]);
    }
    onClose();
  }, [selectedList, multiSelect, onSelect, onClose]);

  const handleSearch = useCallback(({ searchStr }: { searchStr: string }) => setFilter({ search: searchStr }), [
    setFilter,
  ]);
  const handleTypeFilter = useCallback(
    (value: number | string | null) => setFilter({ type: (value as LibraryAsset['type'] | 'all') ?? 'all' }),
    [setFilter]
  );
  const handleFolderClick = (id: string | null) => setFilter({ folderId: id, tag: null });
  const handleTagClick = (tag: string) => setFilter({ tag: state.filters.tag === tag ? null : tag, folderId: null });

  const handleUploadComplete = useCallback(
    (uploaded: LibraryAsset[]) => {
      refetchAssets();
      const eligible = allowedTypes ? uploaded.filter((asset) => allowedTypes.includes(asset.type)) : uploaded;
      if (eligible.length === 0) return;

      setSelectedById((prev) => {
        const next = multiSelect ? new Map(prev) : new Map<string, LibraryAsset>();
        (multiSelect ? eligible : eligible.slice(0, 1)).forEach((asset) => next.set(asset.id, asset));
        return next;
      });
    },
    [refetchAssets, multiSelect, allowedTypes]
  );

  const visibleTags = useMemo(() => Array.from(allTags.entries()).sort((a, b) => b[1] - a[1]), [allTags]);

  const renderFooterStatus = (): JSX.Element => {
    if (multiSelect) {
      const { length } = selectedList;
      return (
        <SelectPrompt>
          {length > 0 ? `${length} asset${length === 1 ? '' : 's'} selected` : 'Select assets or upload new ones'}
        </SelectPrompt>
      );
    }
    if (selectedList.length === 0) {
      return <SelectPrompt>Select an asset or upload a new one</SelectPrompt>;
    }
    const single = selectedList[0];
    return (
      <SelectedPreview>
        <SelectedThumb
          src={single.url}
          imgixParams={{ ...ImgixDefaultSettings, w: 80, h: 80, fit: 'crop', crop: 'edges' }}
          htmlAttributes={{ alt: '' }}
        />
        <SelectedInfo>
          <SelectedName>{single.name}</SelectedName>
          <SelectedMeta>
            {single.width ?? '?'} × {single.height ?? '?'} · {formatBadgeLabel(single.mimeType)}
          </SelectedMeta>
        </SelectedInfo>
      </SelectedPreview>
    );
  };

  const renderFolderNode = (node: FolderNode, depth: number): JSX.Element => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedFolderIds.has(node.id);
    const showChildren = hasChildren && (isExpanded || folderContainsActive(node, state.filters.folderId));

    return (
      <React.Fragment key={node.id}>
        <FolderItem
          $active={state.filters.folderId === node.id}
          $depth={depth}
          onClick={() => handleFolderClick(node.id)}
        >
          {hasChildren ? (
            <ExpandToggle
              onClick={(e) => {
                e.stopPropagation();
                toggleFolderExpand(node.id);
              }}
            >
              {showChildren ? '\u25BC' : '\u25B6'}
            </ExpandToggle>
          ) : (
            <ExpandSpacer />
          )}
          <FolderLabel title={node.name}>{node.name}</FolderLabel>
          <FolderCount>{node.assetCount}</FolderCount>
        </FolderItem>
        {showChildren && node.children.map((child) => renderFolderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onRequestClose={onClose}
      width='960px'
      noPadding
      variant='admin-scrollable-dynamic'
      alignItems='stretch'
      justifyContent='flex-start'
      textAlign='left'
    >
      <ModalInner data-testid='asset-library-modal'>
        <Header>
          <HeaderText>
            <ModalTitle>{title}</ModalTitle>
            <ModalDescription>{description}</ModalDescription>
          </HeaderText>
          <ModalClose onClick={onClose} />
        </Header>

        <ToolbarRow>
          <Flex alignItems='center' style={{ flex: 1, gap: '12px' }}>
            <SearchWrap>
              <Search onUpdate={handleSearch} placeholder='Search assets...' debounced />
            </SearchWrap>
            <DropdownWrap>
              <Dropdown options={typeOptions} selectedValue={state.filters.type} onSelectOption={handleTypeFilter} />
            </DropdownWrap>
          </Flex>
          <ToolbarRight>
            <CountLabel>{totalAssets} assets</CountLabel>
            <UploadButton type='button' onClick={() => setShowUpload(true)}>
              + Upload
            </UploadButton>
          </ToolbarRight>
        </ToolbarRow>

        {error && <Banner $variant='error'>Failed to load assets. {error.message}</Banner>}

        <Body>
          <Sidebar>
            <SectionLabel>FOLDERS</SectionLabel>
            <FolderItem
              $active={state.filters.folderId === null && state.filters.tag === null}
              onClick={() => handleFolderClick(null)}
            >
              <ExpandSpacer />
              <FolderLabel>All Assets</FolderLabel>
              <FolderCount>{totalAssets}</FolderCount>
            </FolderItem>
            {folderTree.map((node) => renderFolderNode(node, 0))}

            {visibleTags.length > 0 && (
              <>
                <Divider />
                <SectionLabel>TAGS</SectionLabel>
                {visibleTags.map(([tag, count]) => (
                  <FolderItem key={tag} $active={state.filters.tag === tag} onClick={() => handleTagClick(tag)}>
                    <TagDot /> {tag} <FolderCount>{count}</FolderCount>
                  </FolderItem>
                ))}
              </>
            )}
          </Sidebar>

          <GridArea>
            {renderGridContent({
              loading,
              assets: displayedAssets,
              selectedIds,
              onToggle: toggleSelected,
              openFilePicker: () => setShowUpload(true),
            })}
          </GridArea>
        </Body>

        <Footer>
          {renderFooterStatus()}
          <FooterActions>
            <CancelButton data-testid='asset-library-cancel' onClick={onClose}>
              Cancel
            </CancelButton>
            <ConfirmButton
              data-testid='asset-library-use-asset'
              onClick={handleConfirm}
              disabled={selectedList.length === 0}
            >
              {multiSelect ? `Add ${selectedList.length} Asset${selectedList.length === 1 ? '' : 's'}` : 'Use Asset'}
            </ConfirmButton>
          </FooterActions>
        </Footer>
      </ModalInner>

      {showUpload && (
        <UploadZone
          enterpriseId={enterpriseId}
          folderId={state.filters.folderId}
          folders={state.folders}
          allowedTypes={allowedTypes}
          onCreateFolder={createFolder}
          onClose={() => setShowUpload(false)}
          onComplete={handleUploadComplete}
        />
      )}
    </ModalContainer>
  );
}

const ModalInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-self: stretch;
  height: 80vh;
  max-height: 720px;
  text-align: left;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 0;
`;

const HeaderText = styled.div``;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #121516;
  margin: 0 0 4px;
`;

const ModalDescription = styled.p`
  font-size: 13px;
  color: #6d747b;
  margin: 0;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #e1e6eb;
`;

const SearchWrap = styled.div`
  flex: 1;
  max-width: 280px;
`;

const DropdownWrap = styled.div`
  position: relative;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CountLabel = styled.span`
  font-size: 12px;
  color: #6d747b;
`;

const Banner = styled.div<{ $variant: 'error' | 'info' }>`
  padding: 8px 24px;
  font-size: 12px;
  background: ${({ $variant }) => ($variant === 'error' ? '#ffebee' : '#e8f4fd')};
  color: ${({ $variant }) => ($variant === 'error' ? '#c62828' : '#0b99e6')};
  border-bottom: 1px solid #e1e6eb;
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 180px;
  min-width: 180px;
  padding: 16px;
  border-right: 1px solid #e1e6eb;
  overflow-y: auto;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #6d747b;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const FolderItem = styled.div<{ $active: boolean; $depth?: number }>`
  display: flex;
  align-items: center;
  padding: 5px 8px 5px ${({ $depth = 0 }) => 8 + $depth * 14}px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  color: ${({ $active }) => ($active ? '#0b99e6' : '#121516')};
  background: ${({ $active }) => ($active ? '#e8f4fd' : 'transparent')};
  cursor: pointer;
  gap: 6px;

  &:hover {
    background: ${({ $active }) => ($active ? '#e8f4fd' : '#f7f8fa')};
  }
`;

const FolderCount = styled.span`
  font-size: 10px;
  color: #6d747b;
  font-weight: 400;
  margin-left: auto;
  flex-shrink: 0;
`;

const FolderLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExpandToggle = styled.span`
  width: 14px;
  flex-shrink: 0;
  font-size: 7px;
  color: #9ba3ab;
  text-align: center;
  cursor: pointer;
  user-select: none;
  line-height: 1;
`;

const ExpandSpacer = styled.span`
  width: 14px;
  flex-shrink: 0;
`;

const TagDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #0b99e6;
  flex-shrink: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #e1e6eb;
  margin: 12px 0;
`;

const GridArea = styled.div`
  flex: 1;
  min-width: 0;
  padding: 16px;
  overflow-y: auto;
  position: relative;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
`;

const Tile = styled.div<{ $selected: boolean }>`
  border: 2px solid ${({ $selected }) => ($selected ? '#0b99e6' : '#e1e6eb')};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #ffffff;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:hover {
    border-color: #0b99e6;
    box-shadow: 0 0 0 1px rgba(11, 153, 230, 0.2);
  }
`;

const TileThumb = styled.div`
  position: relative;
  width: 100%;
  padding-top: 66%;
  background: #f7f8fa;
  overflow: hidden;
`;

const tileImgStyles = `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TileImg = styled(Imgix)<ImgixComponentProps>`
  ${tileImgStyles}
`;

const TileBadge = styled.span`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
`;

const SelectedCheck = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #0b99e6;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const TileInfo = styled.div`
  padding: 6px 8px;
`;

const TileName = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #121516;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TileMeta = styled.div`
  font-size: 10px;
  color: #6d747b;
  margin-top: 1px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 200px;
`;

const EmptyText = styled.div`
  color: #6d747b;
  font-size: 14px;
`;

const EmptyUploadLink = styled.button`
  background: none;
  border: none;
  color: #0b99e6;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-top: 1px solid #e1e6eb;
  background: #f7f8fa;
`;

const SelectedPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const SelectedThumb = styled(Imgix)<ImgixComponentProps>`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid #e1e6eb;
`;

const SelectedInfo = styled.div`
  min-width: 0;
`;

const SelectedName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #121516;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectedMeta = styled.div`
  font-size: 11px;
  color: #6d747b;
`;

const SelectPrompt = styled.span`
  font-size: 13px;
  color: #6d747b;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 16px;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #c4cdd5;
  border-radius: 6px;
  background: #ffffff;
  font-size: 13px;
  font-weight: 600;
  color: #121516;
  cursor: pointer;

  &:hover {
    background: #f7f8fa;
  }
`;

const ConfirmButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: #0b99e6;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: #0988cf;
  }

  &:disabled {
    background: #c4cdd5;
    cursor: not-allowed;
  }
`;
